// @ts-strict-ignore
import { OrgComponentProps, UserAppData } from '../../typescriptHelpers';
import * as React from 'react';
import { tryUpdateOrgSettings, UpdateMergeAccountsSettingRequest } from '../../actions/orgSettings';
import RemoveUserButton from '../RemoveUserButton';
import UpdateUserModal from '../UpdateUserModal';
import InviteTeamContainer from '../inviteTeam/InviteTeamContainer';
import MergeUsersByDomainSetting from './MergeUsersByDomainSetting';
import {
    displayTextForPermission,
    displayTextForUserGroupSingular,
    displayUserNameNoEmail,
} from '../../helpers/displayHelpers';
import Text from 'antd/lib/typography/Text';
import Table, { ColumnsType } from 'antd/lib/table';
import Popover from 'antd/lib/popover';
import { Content } from 'antd/lib/layout/layout';
import { useDispatch, useSelector } from 'react-redux';
import { OrgSettingsUpdatable } from '../../actions/updatable';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { RootState } from '../../reducers/reducers';
import {
    selectAllSupportedVCSIntegrationTypes,
    selectDisabledFeatureData,
    selectDirectorySyncManagesUserGroups,
    selectOrg,
    selectOrgGroups,
    selectOrgProviders,
    selectOrgUsersVCSUserData,
    selectUserAppDataByAppIdMap,
} from '../../selectors/selectors';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { antGrey, runwayColors, tagStyle } from '../styles/globalStyles';
import { SettingOutlined, TeamOutlined } from '@ant-design/icons';
import {
    IntegrationProvider,
    IntegrationProviderId,
    IntegrationProviderType,
    OrgUser,
    OrgUsersVCSUserData,
    SubscriptionFeatureType,
    UserGroupId,
    UserPermissions,
} from '../../types';
import UserSearchComponent from './UserSearchComponent';
import { selectSearchedUsers } from '../../selectors/memoizedSelectors';
import { EffectiveAppInfo, getEffectiveAppsForUser, getUniqueIntegrationProvidersForVCSUserData } from '../../helpers';
import AddNewUsersToAppsSetting from './AddNewUsersToAppsSetting';
import Space from 'antd/lib/space';
import Avatar from 'antd/lib/avatar';
import Tag from 'antd/lib/tag';
import Divider from 'antd/lib/divider';
import EnableACLSetting from './EnableACLSetting';
import OrgTimezoneSetting from './OrgTimezoneSetting';
import OrgCalendarFirstDayOfWeekSetting from './OrgCalendarFirstDayOfWeekSetting';
import { FilterableKeysAndOptions, FilterTableButton } from '../tableFilters/FilterTableButton';
import GroupsTable from './GroupsTable';
import Tooltip from 'antd/lib/tooltip';
import { isNil } from 'lodash';
import { SlackGroupsMappingsTable } from './SlackGroupsMappingsTable';
import { TeamsTagsMappingsTable } from '@/components/orgSettings/TeamsTagsMappingsTable';
import { FetchableEntityTypeV2 } from '@/entityType';
import { globalMessage } from '@/utils/globalMessage';
import { useGetEntityV2 } from '@/utils/hooks/helpers';
import { iconForProviderType } from '../../helpers/integrationHelpers';
import WorkflowsPlatformIcon from '../icons/WorkflowsPlatformIcon';
import VCSUserNameCell from './VCSUserNameCell';
import EditVCSUserNamesModal, { vcsUserNameLabel } from './EditVCSUserNamesModal';
import { getMdmPlatformOverride, platformLabelForApp } from '../../helpers/mdmPlatformOverride';

const MAX_APP_ICONS_SHOWN = 3;
const APP_ICON_SIZE = 18;

const appTooltipTitle = (entry: { info: EffectiveAppInfo; data: UserAppData }) => {
    const appLabel = `${entry.data.appName} (${platformLabelForApp(entry.data)})`;
    if (!entry.info.inherited) {
        return appLabel;
    }

    const sources = entry.info.inheritedFromGroups ?? [];
    const via =
        sources.length === 1 ?
            `via "${sources[0]}" group membership`
        :   `via ${sources.map((name) => `"${name}"`).join(', ')} group memberships`;
    return `${appLabel} — ${via}`;
};

const vcsUserNamesByProviderIdForUser = (
    user: OrgUser | null,
    vcsUserDataByUserEmail: OrgUsersVCSUserData,
): Record<IntegrationProviderId, string> => {
    const vcsUserData = user ? (vcsUserDataByUserEmail[user.email] ?? {}) : {};
    const userNames: Record<IntegrationProviderId, string> = {};
    Object.entries(vcsUserData).forEach(([providerId, data]) => {
        userNames[providerId] = data.username ?? '';
    });
    return userNames;
};

const OrgSettingsTeam: React.FC<OrgComponentProps> = (props: OrgComponentProps) => {
    const org = useSelector((state: RootState) => selectOrg(state, props.orgId));
    const disableEditingApps = org.ssoSamlConfig.isUsingDirectorySyncDefinedApps;
    const [changeUserGroupModalVisible, setChangeUserGroupModalVisible] = React.useState(false);
    const [selectedUser, setSelectedUser] = React.useState(null);
    const [popoversVisibility, setPopoversVisibility] = React.useState({});
    const [vcsUserNamesModalUser, setVCSUserNamesModalUser] = React.useState<OrgUser | null>(null);

    const appDataByAppIdMap = useSelector((state: RootState) => selectUserAppDataByAppIdMap(state, props.orgId));
    const directorySyncManagesGroups = useSelector((state: RootState) =>
        selectDirectorySyncManagesUserGroups(state, props.orgId),
    );

    const dispatch = useDispatch();

    const allowedDomains = useSelector((state: RootState) => selectOrg(state, props.orgId).allowedDomains);
    const user = useSelector((state: RootState) => state.user);
    const searchedUsers = useSelector((state: RootState) => selectSearchedUsers(state, props.orgId));
    const [filteredUsers, setFilteredUsers] = React.useState<OrgUser[]>(searchedUsers);
    const orgIsWriteUserRestricted = useSelector((state: RootState) => {
        const disabledOrLimitedFeature = selectDisabledFeatureData(
            state,
            props.orgId,
            SubscriptionFeatureType.WriteUsers,
        );
        return !isNil(disabledOrLimitedFeature?.limit);
    });
    const orgGroupsById = useSelector((state: RootState) => selectOrgGroups(state, props.orgId));

    const orgProviders = useSelector((state: RootState) => selectOrgProviders(state, props.orgId));
    const slackProviders = orgProviders.filter((provider) => provider.type === IntegrationProviderType.Slack);
    const teamsProviders = orgProviders.filter(
        (provider) => provider.type === IntegrationProviderType.AzureActiveDirectory,
    );

    const allSupportedVCSIntegrationTypes = useSelector(selectAllSupportedVCSIntegrationTypes);
    const vcsProviders = React.useMemo(
        () =>
            getUniqueIntegrationProvidersForVCSUserData(
                orgProviders,
                Object.keys(appDataByAppIdMap),
                allSupportedVCSIntegrationTypes ?? [],
            ),
        [orgProviders, appDataByAppIdMap, allSupportedVCSIntegrationTypes],
    );

    const vcsUserDataByUserEmail = useSelector(selectOrgUsersVCSUserData);
    const [fetchVCSUserData, , fetchVCSUserDataError] = useGetEntityV2(
        FetchableEntityTypeV2.OrgUsersVCSUserData,
        props.orgId,
    );

    React.useEffect(() => {
        if (vcsProviders.length > 0) {
            fetchVCSUserData({ orgId: props.orgId });
        }
    }, [props.orgId, vcsProviders.length]);

    React.useEffect(() => {
        if (fetchVCSUserDataError) {
            globalMessage.error("Couldn't load VCS usernames");
        }
    }, [fetchVCSUserDataError]);

    const renderTitle = (title: string) => {
        return (
            <Text strong style={{ whiteSpace: 'nowrap' }}>
                {title}
            </Text>
        );
    };

    //hide popovers after user actions
    const handlePopoversVisibility = () => {
        setPopoversVisibility({});
    };

    const handleUserGroupModal = () => {
        handlePopoversVisibility();
        setChangeUserGroupModalVisible(true);
    };

    const actionContents = () => {
        return (
            <div style={{ width: 168 }}>
                <PermissionedActionButton
                    type="link"
                    icon={<TeamOutlined style={{ color: antGrey.grey7 }} />}
                    onClick={handleUserGroupModal}
                    style={{ color: antGrey.grey10, display: 'flex', alignItems: 'center' }}
                    actionType={RunwayUserAction.UAEditUserGroups}
                >
                    Edit user
                </PermissionedActionButton>
                <RemoveUserButton
                    selectedUser={selectedUser}
                    user={user}
                    orgId={props.orgId}
                    isDirectorySyncEnabled={org.ssoSamlConfig?.ssoDirectory?.isActive ?? false}
                    onUserRemoved={handlePopoversVisibility}
                />
            </div>
        );
    };

    const renderActions = (user: OrgUser, index: number) => {
        return (
            <Popover
                overlayClassName="org-settings-popover" //some overrides in index.less for component's inner elements
                overlayStyle={{ paddingBottom: '0px' }}
                trigger="click"
                open={popoversVisibility[index] ?? false}
                onOpenChange={(open) => {
                    const updatedPopoverVisibility = popoversVisibility;
                    updatedPopoverVisibility[index] = open;

                    setPopoversVisibility(updatedPopoverVisibility);

                    open ? setSelectedUser(user) : setSelectedUser(null);
                }}
                placement="topRight"
                content={actionContents}
                destroyTooltipOnHide={true}
            >
                <SettingOutlined style={{ fontSize: '17px', paddingInlineEnd: '30px', color: antGrey.grey8 }} />
            </Popover>
        );
    };

    const columns: ColumnsType = [
        {
            title: renderTitle('Name'),
            render: (value: OrgUser) => {
                const userName = displayUserNameNoEmail(value);
                if (!userName) {
                    return <Text type="secondary">No name</Text>;
                }
                return <Text key={value.id}>{userName}</Text>;
            },
        },
        {
            title: renderTitle('Email'),
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: renderTitle('Permissions'),
            render: (user: OrgUser) => {
                if (!user.permissions || user.permissions.length === 0) {
                    if (orgIsWriteUserRestricted) {
                        return <Tag style={{ ...tagStyle, color: antGrey.grey7 }}>None (read-only)</Tag>;
                    }

                    return <Text type="secondary">None</Text>;
                }

                return (
                    <Space wrap>
                        {user.permissions.map((permission, index) => {
                            const permissionText =
                                orgIsWriteUserRestricted && permission === UserPermissions.Admin ?
                                    'Admin (read-write)'
                                :   displayTextForPermission(permission);

                            return (
                                <Tag key={index} style={{ ...tagStyle, color: runwayColors.primary }}>
                                    {permissionText}
                                </Tag>
                            );
                        })}
                    </Space>
                );
            },
        },
        {
            title: renderTitle('User groups'),
            render: (user: OrgUser) => {
                if (!user.groups || user.groups.length === 0) {
                    return <Text type="secondary">No groups</Text>;
                }

                const allGroupNames = user.groups.map((groupId) =>
                    displayTextForUserGroupSingular(orgGroupsById[groupId]),
                );
                const maxVisible = 10;
                const visibleGroups = allGroupNames.slice(0, maxVisible);
                const remainingGroups = allGroupNames.slice(maxVisible);

                return (
                    <Text>
                        {visibleGroups.join(', ')}
                        {remainingGroups.length > 0 && (
                            <Tooltip
                                title={
                                    <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                                        {allGroupNames.map((name, index) => (
                                            <div key={index}>{name}</div>
                                        ))}
                                    </div>
                                }
                            >
                                <span>, +{remainingGroups.length}</span>
                            </Tooltip>
                        )}
                    </Text>
                );
            },
        },
        {
            title: renderTitle('Apps'),
            render: (user: OrgUser) => {
                const effectiveApps = getEffectiveAppsForUser(user, orgGroupsById);
                const enrichedApps = effectiveApps
                    .map((info) => ({ info, data: appDataByAppIdMap[info.appId] }))
                    .filter((entry) => !!entry.data)
                    .sort(
                        (a, b) =>
                            a.data.appName.localeCompare(b.data.appName) ||
                            a.data.platform.localeCompare(b.data.platform),
                    );

                if (enrichedApps.length === 0) {
                    return <Text type="secondary">No apps</Text>;
                }

                const appsToDisplay = enrichedApps.slice(0, MAX_APP_ICONS_SHOWN);
                const remainingApps = enrichedApps.slice(MAX_APP_ICONS_SHOWN);

                return (
                    <Space size={4} wrap style={{ rowGap: 4 }}>
                        {appsToDisplay.map((entry) => (
                            <Tooltip key={entry.info.appId} title={appTooltipTitle(entry)}>
                                {/* inherited access is dimmed, the same signal the app name used to carry */}
                                <span style={{ opacity: entry.info.inherited ? 0.5 : 1, display: 'flex' }}>
                                    {entry.data.appImageUrl ?
                                        <Avatar
                                            shape="square"
                                            size={APP_ICON_SIZE}
                                            src={entry.data.appImageUrl}
                                            style={{ borderRadius: 4, flexShrink: 0 }}
                                        >
                                            {entry.data.appName.charAt(0).toUpperCase()}
                                        </Avatar>
                                    :   (getMdmPlatformOverride(entry.data.shouldDisplayAsMdm)?.icon(APP_ICON_SIZE) ?? (
                                            <WorkflowsPlatformIcon
                                                platform={entry.data.platform}
                                                width={APP_ICON_SIZE}
                                                height={APP_ICON_SIZE}
                                            />
                                        ))
                                    }
                                </span>
                            </Tooltip>
                        ))}
                        {remainingApps.length > 0 && (
                            <Tooltip
                                title={
                                    <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                                        {remainingApps.map((entry) => (
                                            <div key={entry.info.appId}>{appTooltipTitle(entry)}</div>
                                        ))}
                                    </div>
                                }
                            >
                                <Text type="secondary">+{remainingApps.length}</Text>
                            </Tooltip>
                        )}
                    </Space>
                );
            },
        },
        ...vcsProviders.map((vcsProvider: IntegrationProvider) => {
            const icon = iconForProviderType(vcsProvider.type);

            return {
                title: (
                    <Tooltip title={vcsUserNameLabel(vcsProvider, vcsProviders)}>
                        {icon ?
                            <img src={icon} width={18} height={18} style={{ verticalAlign: 'middle' }} />
                        :   renderTitle(`${vcsProvider.name} username`)}
                    </Tooltip>
                ),
                key: vcsProvider.id,
                align: 'center' as const,
                render: (user: OrgUser) => (
                    <VCSUserNameCell
                        user={user}
                        orgId={props.orgId}
                        vcsUserName={vcsUserDataByUserEmail[user.email]?.[vcsProvider.id]?.username ?? ''}
                        onEdit={() => setVCSUserNamesModalUser(user)}
                    />
                ),
            };
        }),
        {
            title: '',
            align: 'right',
            render: (user: OrgUser, _, index) => {
                return renderActions(user, index);
            },
        },
    ];
    const onUpdateMergeAccountsSetting = (checked: boolean) => {
        const request: UpdateMergeAccountsSettingRequest = {
            orgId: props.orgId,
            mergeAccountsWithAllowedDomainsEnabled: checked,
        };

        dispatch(tryUpdateOrgSettings(OrgSettingsUpdatable.MergeAccountsSetting, request));
    };

    const showMergeAccountsSetting = allowedDomains.length > 0;

    const filterableKeysAndOptions = React.useMemo(() => {
        const options: FilterableKeysAndOptions<OrgUser> = {
            permissions: {
                displayName: 'Permissions',
                displayNameForPossibleValue: (value) => {
                    return displayTextForPermission(value as UserPermissions);
                },
                defaultValues: [UserPermissions.Admin, UserPermissions.ITAdmin],
            },
            groups: {
                displayName: 'User groups',
                displayNameForPossibleValue: (value: UserGroupId) => {
                    return displayTextForUserGroupSingular(orgGroupsById[value]);
                },
                defaultValues: Object.keys(orgGroupsById),
                showAsDropdown: true,
            },
            appIds: {
                displayName: 'Apps',
                displayNameForPossibleValue: (value: string) => {
                    const appData = appDataByAppIdMap[value];
                    if (!appData) return 'Unknown';
                    return `${appData.appName} ${appData.platform ? `(${platformLabelForApp(appData)})` : ''}`;
                },
                defaultValues: Object.keys(appDataByAppIdMap),
                extractPossibleValueOrValuesFromItem: (item) => {
                    //remove apps that are not in the appDataByAppIdMap
                    return getEffectiveAppsForUser(item, orgGroupsById)
                        .map((info) => info.appId)
                        .filter((appId) => appDataByAppIdMap[appId]);
                },
                showAsDropdown: true,
            },
        };
        return options;
    }, [appDataByAppIdMap, orgGroupsById]);

    return (
        <Content>
            <div
                style={{
                    maxWidth: 900,
                    width: '100%',
                }}
            >
                <AddNewUsersToAppsSetting orgId={props.orgId} />
                {showMergeAccountsSetting && (
                    <>
                        <Divider />
                        <MergeUsersByDomainSetting
                            orgId={props.orgId}
                            onUpdateMergeAccountsSetting={onUpdateMergeAccountsSetting}
                        />
                    </>
                )}
                <Divider />
                <EnableACLSetting orgId={props.orgId} />
                <Divider />
                <OrgTimezoneSetting orgId={props.orgId} />
                <Divider />
                <OrgCalendarFirstDayOfWeekSetting orgId={props.orgId} />
            </div>

            <div
                style={{
                    display: 'flex',
                    gap: 20,
                    alignItems: 'center',
                    marginTop: 30,
                }}
            >
                <Text
                    style={{
                        fontSize: 20,
                        color: antGrey.grey7,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {'Users'}
                </Text>
                <div
                    style={{
                        flex: 1,
                        height: '100%',
                    }}
                >
                    <Divider
                        type="horizontal"
                        style={{
                            width: '100%',
                            margin: 0,
                        }}
                    />
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 15, paddingInlineEnd: 16 }}>
                <UserSearchComponent />
                <Space
                    size={1}
                    style={{
                        flex: 1,
                        padding: '0 1rem',
                    }}
                >
                    <FilterTableButton<OrgUser>
                        filterableKeysAndOptions={filterableKeysAndOptions}
                        onChange={(filteredUsers) => {
                            setFilteredUsers(filteredUsers);
                        }}
                        items={searchedUsers}
                    />
                </Space>
                <InviteTeamContainer orgId={props.orgId} />
            </div>
            <Table
                dataSource={filteredUsers.map((user) => {
                    return { ...user, key: user.id };
                })}
                columns={columns}
                pagination={{ hideOnSinglePage: true, defaultPageSize: 10 }}
                style={{ marginTop: 24, marginBottom: 24 }}
                scroll={{ x: 1100 }}
            />
            <UpdateUserModal
                open={changeUserGroupModalVisible}
                appDataByAppIdMap={appDataByAppIdMap}
                onCancel={() => setChangeUserGroupModalVisible(false)}
                onSuccess={() => setChangeUserGroupModalVisible(false)}
                currentUser={selectedUser}
                orgId={props.orgId}
                directorySyncManagesGroups={directorySyncManagesGroups}
                isUsingAdHocGroups={org.orgSettings?.isUsingAdHocGroups ?? false}
                disableEditingApps={disableEditingApps}
                usePermissionsOnGroupLevel={org.usePermissionsOnGroupLevel}
            />
            <EditVCSUserNamesModal
                open={vcsUserNamesModalUser !== null}
                orgId={props.orgId}
                user={vcsUserNamesModalUser}
                vcsProviders={vcsProviders}
                vcsUserNamesByProviderId={vcsUserNamesByProviderIdForUser(
                    vcsUserNamesModalUser,
                    vcsUserDataByUserEmail,
                )}
                onCancel={() => setVCSUserNamesModalUser(null)}
                onSaved={() => {
                    setVCSUserNamesModalUser(null);
                    fetchVCSUserData({ orgId: props.orgId });
                }}
            />
            <GroupsTable orgId={props.orgId} />
            {slackProviders.map((provider) => (
                <SlackGroupsMappingsTable
                    orgId={props.orgId}
                    providerId={provider.id}
                    slackTeamName={provider.data?.slackTeam}
                    key={provider.id}
                />
            ))}
            {teamsProviders.map((provider) => (
                <TeamsTagsMappingsTable orgId={props.orgId} providerId={provider.id} key={provider.id} />
            ))}
        </Content>
    );
};

export default OrgSettingsTeam;
